import {
  LifeReadinessCategory,
  LifeReadinessSubcategory,
  PaginationProps,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';

type NavigationButtonReturn = Readonly<{
  route: string;
}>;

type NavigationButtonProps = Readonly<{
  allCategories: LifeReadinessCategory[];
  allSubcategories: LifeReadinessSubcategory[];
  currentCategory: LifeReadinessCategory;
  currentSubcategory: LifeReadinessSubcategory;
}>;

const getSubcategoriesForCategory = (
  allSubcategories: LifeReadinessSubcategory[],
  categoryId: number,
) =>
  allSubcategories
    .filter((sub) => sub.life_survey_category_id === categoryId)
    .sort((a, b) => a.sequence - b.sequence);

const getAllOrderedPages = (
  categories: LifeReadinessCategory[],
  subcategories: LifeReadinessSubcategory[],
) => {
  if (!categories || !subcategories) return [];

  const pages = [];
  const sortedCategories = [...categories].sort((a, b) => a.sequence - b.sequence);

  for (const category of sortedCategories) {
    const categorySubcategories = subcategories
      .filter((s) => s.life_survey_category_id === category.id)
      .sort((a, b) => a.sequence - b.sequence);

    for (const sub of categorySubcategories) {
      pages.push({
        category,
        subcategory: sub,
        route: `${APP_ROUTE.LIFE_READINESS}/${category.slug}/${sub.name.toLowerCase()}`,
      });
    }
  }

  return pages;
};

const handlePreviousButton = ({
  allCategories,
  allSubcategories,
  currentCategory,
  currentSubcategory,
}: NavigationButtonProps): NavigationButtonReturn => {
  const isFirstSubcategory = currentSubcategory.sequence === 0;

  if (isFirstSubcategory) {
    const prevCategory = allCategories.find((cat) => cat.sequence === currentCategory.sequence - 1);

    if (!prevCategory) {
      return { route: `${APP_ROUTE.LIFE_READINESS}/quiz` };
    }

    const prevSubs = getSubcategoriesForCategory(allSubcategories, prevCategory.id);
    const lastSub = prevSubs.at(-1);

    return lastSub
      ? {
          route: `${APP_ROUTE.LIFE_READINESS}/${prevCategory.slug}/${lastSub.name.toLowerCase()}`,
        }
      : { route: APP_ROUTE.LIFE_READINESS };
  }

  const prevSub = getSubcategoriesForCategory(allSubcategories, currentCategory.id).find(
    (sub) => sub.sequence === currentSubcategory.sequence - 1,
  );

  return prevSub
    ? {
        route: `${APP_ROUTE.LIFE_READINESS}/${currentCategory.slug}/${prevSub.name.toLowerCase()}`,
      }
    : { route: APP_ROUTE.LIFE_READINESS };
};

const handleNextButton = ({
  allCategories,
  allSubcategories,
  currentCategory,
  currentSubcategory,
}: NavigationButtonProps): NavigationButtonReturn => {
  const subcategories = getSubcategoriesForCategory(allSubcategories, currentCategory.id);
  const nextSubcategory = subcategories.find(
    (sub) => sub.sequence === currentSubcategory.sequence + 1,
  );

  if (nextSubcategory) {
    // if there is a next subcategory in this category, navigate to it
    return {
      route: `${APP_ROUTE.LIFE_READINESS}/${currentCategory.slug}/${nextSubcategory.name.toLowerCase()}`,
    };
  }

  // if there is no next subcategory in this category, navigate to the next category if it exists
  const nextCategory = allCategories.find((cat) => cat.sequence === currentCategory.sequence + 1);

  if (nextCategory) {
    const nextCategorySubs = getSubcategoriesForCategory(allSubcategories, nextCategory.id);
    const firstSubcategory = nextCategorySubs[0];

    return firstSubcategory
      ? {
          route: `${APP_ROUTE.LIFE_READINESS}/${nextCategory.slug}/${firstSubcategory.name.toLowerCase()}`,
        }
      : { route: APP_ROUTE.LIFE_READINESS };
  }

  return {
    route: APP_ROUTE.LIFE_READINESS,
  };
};

export const getPaginationProps = (
  categories: LifeReadinessCategory[],
  subcategories: LifeReadinessSubcategory[],
  currentCategory: LifeReadinessCategory,
  currentSubcategory: LifeReadinessSubcategory,
  showExplainerCard: boolean,
  navigate: (route: string) => void,
): PaginationProps => {
  const allOrderedPages = getAllOrderedPages(categories, subcategories);
  const currentPageIndex = allOrderedPages.findIndex(
    (page) =>
      page.category.id === currentCategory.id && page.subcategory.id === currentSubcategory.id,
  );

  const currentPage = currentPageIndex + 1; // 1-indexed
  const totalPages = allOrderedPages.length;

  const onPageChange = (page: number) => {
    const targetPage = allOrderedPages[page - 1];

    if (targetPage) {
      navigate(targetPage.route);
    }
  };

  const backButtonParams = handlePreviousButton({
    allCategories: categories,
    allSubcategories: subcategories,
    currentCategory,
    currentSubcategory,
  });

  const nextButtonParams = handleNextButton({
    allCategories: categories,
    allSubcategories: subcategories,
    currentCategory,
    currentSubcategory,
  });

  const handlePrevPage = () => {
    navigate(backButtonParams.route);
  };

  const handleNextPage = () => {
    if (showExplainerCard) {
      // navigate to the first subcategory of the first category
      navigate(allOrderedPages[0].route);

      return;
    }

    navigate(nextButtonParams.route);
  };

  return {
    currentPage,
    totalPages,
    onPageChange,
    handlePrevPage,
    handleNextPage,
    maxWidth: 1280 + 72,
    withPageNumbers: false,
  };
};
